// Top-level error boundary. A React render error anywhere in the tree falls
// through to this fallback instead of unmounting the app to a blank page.
// It uses plain <a> and window.location (not <Link>) so the fallback still
// works even when the failure is in the router itself. Wrap <App/> with this
// in main.tsx.
import React from "react";
import { JobWarpIcon } from "./JobWarpIcon.js";

type Props = { children: React.ReactNode };
type State = { hasError: boolean };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    // Surface it in the console for local debugging. No external error
    // reporter is wired up yet; add one here when we have one.
    // eslint-disable-next-line no-console
    console.error("[jobwarp] uncaught render error", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex flex-col bg-page">
        <header className="border-b border-default">
          <div className="max-w-[960px] mx-auto px-6 py-4 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2 text-primary">
              <JobWarpIcon size={20} />
              <span className="font-serif italic text-[20px] tracking-[-0.01em] leading-none">JobWarp</span>
            </a>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-[420px]">
            <div className="font-mono text-tertiary text-[64px] leading-none tracking-mono">500</div>
            <h1 className="text-28 font-medium text-primary mt-6">Something went wrong</h1>
            <p className="text-secondary text-sm mt-3">
              The page hit an unexpected error. Reloading usually clears it. If it
              keeps happening, head back to start.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="btn-primary"
              >
                Reload page
              </button>
              <a href="/" className="btn-secondary">Back to home</a>
            </div>
          </div>
        </main>

        <footer className="border-t border-default">
          <div className="max-w-[960px] mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-4 text-tertiary text-11 font-mono uppercase tracking-mono-caps">
            <span>© 2026 JobWarp · Built for job seekers</span>
            <span className="flex gap-4">
              <a href="/privacy/" className="hover:text-primary">Privacy</a>
              <a href="/terms/" className="hover:text-primary">Terms</a>
            </span>
          </div>
        </footer>
      </div>
    );
  }
}
